import { Fragment, useState } from "react";
import { Container, Form, FormGroup, Label, Input, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { toast } from "react-toastify";

export const UserCreate = () => {
  const navigate = useNavigate();
  const [emailConflict, setEmailConflict] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      referral_code: "",
      name: "",
      email: "",
      phone: "",
      document: "",
      country_id: "",
      send_email: true,
    },
  });

  const referralCode = watch("referral_code");
  const { getData, postData } = useDataFetch();

  const { data: countriesData, isLoading: isLoadingCountries } = getData(
    ["countries"],
    "/product/all/countries",
    { enabled: true }
  );

  const countries = countriesData?.data?.countries || [];

  const {
    data: referralData,
    error: referralError,
    refetch: refetchReferral,
  } = getData(
    [`referralCode-${referralCode}`],
    referralCode ? `/users/get/multyhealth_code/${referralCode}` : "",
    { enabled: false }
  );

  const { mutateAsync: createUser, isPending } = postData("/users");

  const handleReferralBlur = () => {
    if (referralCode) refetchReferral();
  };

  const onSubmit = async (data) => {
    const payload = {
      email: data.email,
      name: data.name,
      phone: data.phone || null,
      document: data.document,
      reference_id: data.referral_code,
      country_id: data.country_id,
      send_email: data.send_email,
    };

    setEmailConflict(null);

    try {
      await createUser(payload);
      toast.success("Cliente creado exitosamente");
      navigate("/user");
    } catch (error) {
      toast.error("El correo ya está en uso");
    }
  };

  const errorMsg = (err) => err && <div style={{ color: "#DC2626", fontSize: 12, marginTop: 4 }}>{err.message}</div>;

  return (
    <Fragment>
      <Container fluid={true} className="ui-content" style={{ padding: 0 }}>
        <button type="button" className="ui-back" onClick={() => navigate(-1)}>
          <i className="ri-arrow-left-line" /> Volver a usuarios
        </button>
        <h1 className="ui-page-header__title" style={{ marginBottom: 16 }}>Registrar cliente</h1>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="ui-card" style={{ maxWidth: 720 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <FormGroup className="mb-0">
                <Label>Código de referido <span style={{ color: "#DC2626" }}>*</span></Label>
                <Controller
                  name="referral_code"
                  control={control}
                  rules={{ required: "Este campo es obligatorio" }}
                  render={({ field }) => (
                    <Input {...field} type="text" invalid={!!errors.referral_code} onBlur={handleReferralBlur} placeholder="Código de referido" />
                  )}
                />
                {errorMsg(errors.referral_code)}
                {referralError && <div style={{ color: "#DC2626", fontSize: 12, marginTop: 4 }}>Código inválido</div>}
                {referralData?.data?.user && (
                  <div style={{ color: "#0F9A5A", fontSize: 12, marginTop: 4 }}>
                    Referido por: <strong>{referralData.data.user.name}</strong> · {referralData.data.user.email}
                  </div>
                )}
              </FormGroup>

              <FormGroup className="mb-0">
                <Label>País <span style={{ color: "#DC2626" }}>*</span></Label>
                <Controller
                  name="country_id"
                  control={control}
                  rules={{ required: "El país es obligatorio" }}
                  render={({ field }) => (
                    <Input
                      type="select"
                      invalid={!!errors.country_id}
                      {...field}
                      onChange={(e) => {
                        const selected = countries.find((c) => c.id === e.target.value);
                        if (selected) field.onChange(selected.id);
                      }}
                      disabled={isLoadingCountries}
                    >
                      <option value="">Selecciona un país</option>
                      {isLoadingCountries ? (
                        <option>Cargando...</option>
                      ) : (
                        countries.map((country) => (
                          <option key={country.id} value={country.id}>{country.name}</option>
                        ))
                      )}
                    </Input>
                  )}
                />
                {errorMsg(errors.country_id)}
              </FormGroup>

              <FormGroup className="mb-0">
                <Label>Documento <span style={{ color: "#DC2626" }}>*</span></Label>
                <Controller
                  name="document"
                  control={control}
                  rules={{ required: "El documento es obligatorio" }}
                  render={({ field }) => <Input {...field} type="text" invalid={!!errors.document} placeholder="Número de documento" />}
                />
                {errorMsg(errors.document)}
              </FormGroup>

              <FormGroup className="mb-0">
                <Label>Nombre <span style={{ color: "#DC2626" }}>*</span></Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "El nombre es obligatorio" }}
                  render={({ field }) => <Input {...field} type="text" invalid={!!errors.name} placeholder="Nombre completo" />}
                />
                {errorMsg(errors.name)}
              </FormGroup>

              <FormGroup className="mb-0">
                <Label>Correo <span style={{ color: "#DC2626" }}>*</span></Label>
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: "El correo es obligatorio" }}
                  render={({ field }) => <Input {...field} type="email" invalid={!!errors.email} placeholder="ejemplo@correo.com" />}
                />
                {errorMsg(errors.email)}
              </FormGroup>

              <FormGroup className="mb-0">
                <Label>Teléfono</Label>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => <Input {...field} type="text" placeholder="Opcional" />}
                />
              </FormGroup>
            </div>

            <div style={{ marginTop: 14 }}>
              <Controller
                name="send_email"
                control={control}
                render={({ field }) => (
                  <Label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                    <Input
                      type="checkbox"
                      style={{ margin: 0 }}
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                    Enviar correo de registro al cliente
                  </Label>
                )}
              />
            </div>

            <div style={{ display: "flex", gap: 11, marginTop: 22, paddingTop: 18, borderTop: "1px solid #EDEFF6" }}>
              <button
                type="button"
                onClick={() => navigate(-1)}
                style={{ border: "1px solid #E1E4F0", background: "#fff", color: "#3B4266", borderRadius: 9, padding: "12px 20px", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isPending}
                style={{ flex: 1, border: "none", background: "#4B36F0", color: "#fff", borderRadius: 9, padding: "12px 0", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: isPending ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              >
                {isPending ? <Spinner size="sm" /> : "Guardar cliente"}
              </button>
            </div>
          </div>
        </Form>
      </Container>
    </Fragment>
  );
};
